import { Navigate, Outlet, useLocation, useNavigate } from "react-router";
import { useAuth } from "@/modules/auth";
import { usePageLoader } from "@/contexts/page-loader-context";
import { useBrands } from "@/modules/brand";
import { useEffect, useRef, useState } from "react";

export default function SetupGuard() {
	const { isNameAvailable, isBrandAvailable, currentWorkspaceId, loading, updateProfile, setWorkspaceId } = useAuth();
	const { indexBrands } = useBrands();
	const navigate = useNavigate();

	const { showLoader, hideLoader } = usePageLoader();
	const location = useLocation();
	const searchParams = new URLSearchParams(location.search);
	const redirect = searchParams.get("redirect");
	const redirectQuery = redirect ? `?redirect=${encodeURIComponent(redirect)}` : "";

	const [autoSelecting, setAutoSelecting] = useState(false);
	const autoSelectAttemptedRef = useRef(false);

	// UI-only loader side effect
	useEffect(() => {
		if (loading || autoSelecting) {
			showLoader();
		} else {
			hideLoader();
		}
	}, [loading, autoSelecting, showLoader, hideLoader]);

	// Auto-select workspace when currentWorkspaceId is blank
	useEffect(() => {
		if (loading || !isNameAvailable || !isBrandAvailable || currentWorkspaceId) return;
		if (autoSelectAttemptedRef.current || autoSelecting) return;

		autoSelectAttemptedRef.current = true;
		setAutoSelecting(true);

		const autoSelectWorkspace = async () => {
			const brands = await indexBrands();
			if (brands && brands.length === 1) {
				const brandId = brands[0].id;
				await updateProfile({ current_workspace_id: brandId });
				setWorkspaceId(brandId);
				navigate(redirect || `/${brandId}/dashboard`, { replace: true });
			} else {
				navigate("/brands", { replace: true });
			}
			setAutoSelecting(false);
		};

		autoSelectWorkspace();
	}, [loading, isNameAvailable, isBrandAvailable, currentWorkspaceId, autoSelecting, indexBrands, updateProfile, setWorkspaceId, navigate, redirect]);

	// 1️⃣ Still resolving state
	if (loading || autoSelecting) {
		return null;
	}

	// 2️⃣ Profile not complete
	if (!isNameAvailable) {
		if (location.pathname !== "/onboarding/complete-profile") {
			return <Navigate to={`/onboarding/complete-profile${redirectQuery}`} replace />;
		}
		return <Outlet />;
	}

	// 3️⃣ Profile complete, brand missing
	if (isNameAvailable && !isBrandAvailable) {
		if (location.pathname !== "/onboarding/create-brand") {
			return <Navigate to={`/onboarding/create-brand${redirectQuery}`} replace />;
		}
		return <Outlet />;
	}

	// 4️⃣ Setup complete → workspace dashboard or brand picker
	if (isNameAvailable && isBrandAvailable) {
		if (redirect) {
			return <Navigate to={redirect} replace />;
		}
		if (currentWorkspaceId) {
			return <Navigate to={`/${currentWorkspaceId}/dashboard`} replace />;
		}
		// Auto-select effect will handle this
		return null;
	}

	// 5️⃣ Fallback (rare, but safe)
	return null;
}
